import { useState, useEffect, useRef, useMemo } from 'react';
import { SiteVideoData } from '../../../interfaces';

interface UseVerticalVideoPlayerProps {
    itemId: string;
    index: number;
    media: SiteVideoData;
    isGalleryView: boolean;
    showPrerollAds: boolean;
    hidePrerollAds?: boolean;
    prerollIndex?: number;
    currentPlayingVideo?: number;
    onNextSlide?: () => void;
    enabled?: boolean;
}

export const useVerticalVideoPlayer = (props: UseVerticalVideoPlayerProps) => {
    const { itemId, index, media, isGalleryView, showPrerollAds, hidePrerollAds, currentPlayingVideo, onNextSlide, enabled = true } = props;

    const [isWaitingForAd, setIsWaitingForAd] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const playerApiRef: any = useRef<any>(undefined);
    const observerRef: { current: MutationObserver | null } = useRef<MutationObserver>(null);
    const observeRetryTimerRef: { current: number | undefined } = useRef<number | undefined>(undefined);
    const observeTimeoutRef: { current: number | undefined } = useRef<number | undefined>(undefined);

    const hasPrerollAds = enabled && !!showPrerollAds && !hidePrerollAds;
    const allowManualAutoplay = enabled && media ? !!(media as SiteVideoData).isAutoPlaying : false;
    const playerMedia: SiteVideoData | null = useMemo(() => enabled && media ? ({ ...media, isAutoPlaying: false }) as SiteVideoData : null, [media, enabled]);
    const shouldDisplayVideo = enabled && (isGalleryView && currentPlayingVideo !== undefined
        ? (currentPlayingVideo === index || currentPlayingVideo === index - 1 || currentPlayingVideo === index + 1)
        : true);

    const observeCleanup = () => {
        clearTimeout(observeRetryTimerRef.current);
        clearTimeout(observeTimeoutRef.current);
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    };

    const observeAndMoveVideoElements = (retryCount = 0) => {
        const targetNode = document.getElementById("VerticalVideoItem_" + itemId);
        if (!targetNode) {
            if (retryCount < 20) { // retry up to 2 seconds
                observeRetryTimerRef.current = window.setTimeout(() => observeAndMoveVideoElements(retryCount + 1), 100);
            }
            return;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        clearTimeout(observeTimeoutRef.current);

        const maxObserveTime = 10000;
        observeTimeoutRef.current = window.setTimeout(() => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        }, maxObserveTime);

        observerRef.current = new MutationObserver(() => {
            const pairs = [
                { source: ".fp-elapsed", target: "#elapsed-target" },
                { source: ".fp-duration", target: "#duration-target" },
                { source: ".fp-timeline", target: "#timeline-target" },
                { source: ".fp-volume-control", target: "#volume-target" },
            ];

            let allHandled = true;

            for (const { source, target } of pairs) {
                const sourceEl = targetNode.querySelector(source);
                const targetEl = targetNode.querySelector(target);

                if (!targetEl) continue; // design doesn't use this element — skip
                if (!sourceEl) { allHandled = false; continue; } // source not rendered yet — wait
                if (targetEl.contains(sourceEl)) continue; // already moved

                targetEl.innerHTML = "";
                targetEl.appendChild(sourceEl);
            }

            if (allHandled && observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
                clearTimeout(observeTimeoutRef.current);
            }
        });

        observerRef.current.observe(targetNode, {
            childList: true,
            subtree: true,
        });
    };

    const handleVideoEnded = (e) => {
        e.target.currentTime = 0;
        isGalleryView && onNextSlide ? onNextSlide() : e.target.click();
    };

    const onPlayerLoaded = (player) => {
        const p: any = player as any;
        const coerced = (p && typeof p.play === "function")
            ? p
            : (p && p.api && typeof p.api.play === "function")
                ? p.api
                : (p && p[0] && typeof p[0].play === "function")
                    ? p[0]
                    : p;

        playerApiRef.current = coerced;
        setPlayerReady(true);
    };

    // Initial mount: observe and move video elements
    useEffect(() => {
        if (!enabled) return;
        observeAndMoveVideoElements();

        return () => {
            observeCleanup();
        };
    }, [enabled]);

    // Re-observe when current playing video changes
    useEffect(() => {
        if (!enabled) return;
        if (currentPlayingVideo === index) {
            observeAndMoveVideoElements();
        }
        return () => {
            observeCleanup();
        };
    }, [currentPlayingVideo, enabled]);

    // Main playback control effect
    useEffect(() => {
        if (!enabled) return;
        if (!isGalleryView || currentPlayingVideo === undefined) return;

        const isActive = currentPlayingVideo === index;

        let isDisposed = false;
        let adStarted = false;
        let playTriggered = false;
        let completedListenerAttached = false;
        let contentLockIntervalId: number | undefined;
        let fallbackTimeoutId: number | undefined;
        let waitIntervalId: number | undefined;

        const safeCall = (fn: () => void) => {
            try {
                fn();
            } catch { }
        };

        const safeSetCurrentTime = (videoEl: HTMLVideoElement, time: number) => {
            safeCall(() => { videoEl.currentTime = time; });
        };

        const safeVideoPlay = (videoEl: HTMLVideoElement) => {
            safeCall(() => {
                const p: any = videoEl.play();
                if (p && typeof p.catch === "function") p.catch(() => { });
            });
        };

        const patchVideoPlay = () => {
            const videoEl = getContentVideoEl();
            if (!videoEl || (videoEl as any).__playPatched) return;
            const origPlay = videoEl.play.bind(videoEl);
            videoEl.play = function () {
                const p: any = origPlay();
                if (p && typeof p.catch === "function") p.catch(() => { });
                return p;
            };
            (videoEl as any).__playPatched = true;
        };

        const safeApiPlay = (api: any) => {
            if (!api || typeof api.play !== "function") return;

            // Patch the underlying <video>.play() so that Flowplayer's internal
            // setTimeout → video.play() calls always have their rejections caught.
            patchVideoPlay();

            try {
                const p: any = api.play();
                if (p && typeof p.catch === "function") p.catch(() => { });
            } catch { }
        };

        const safeApiPause = (api: any) => {
            if (!api || typeof api.pause !== "function") return;
            safeCall(() => { api.pause(); });
        };

        const safeApiSetOpts = (api: any, opts: any) => {
            if (!api || typeof api.setOpts !== "function") return;
            safeCall(() => { api.setOpts(opts); });
        };

        const coerceFlowplayerApi = (p: any) => {
            if (!p) return null;
            if (typeof p.play === "function") return p;
            if (p.api && typeof p.api.play === "function") return p.api;
            if (p[0] && typeof p[0].play === "function") return p[0];
            return null;
        };

        const resolveFlowplayerApiFromDom = () => {
            try {
                const root = document.getElementById(`VerticalVideoItem_${itemId}`);
                if (!root) return null;
                const playerEl = root.querySelector("#player") as any;
                if (!playerEl) return null;

                const fp = typeof window !== "undefined" ? (window as any).flowplayer : null;
                if (typeof fp !== "function") return null;

                return coerceFlowplayerApi(fp(playerEl));
            } catch {
                return null;
            }
        };

        const getContentVideoEl = () => {
            const root = document.getElementById(`VerticalVideoItem_${itemId}`);
            if (!root) return null;
            const playerEl = root.querySelector("#player") as HTMLElement;
            if (!playerEl) return null;
            const contentEngineEl = Array.from(playerEl.children).find((child) =>
                (child as HTMLElement).classList && (child as HTMLElement).classList.contains("fp-engine")
            ) as any;
            if (contentEngineEl && contentEngineEl.tagName === "VIDEO") return contentEngineEl as HTMLVideoElement;
            return null;
        };

        const clearAdWaitTimers = () => {
            if (contentLockIntervalId) {
                clearInterval(contentLockIntervalId);
                contentLockIntervalId = undefined;
            }
            if (fallbackTimeoutId) {
                clearTimeout(fallbackTimeoutId);
                fallbackTimeoutId = undefined;
            }
            if (!isDisposed) setIsWaitingForAd(false);
        };

        const isAdContainerActive = () => {
            const root = document.getElementById(`VerticalVideoItem_${itemId}`);
            if (!root) return false;
            const playerEl = root.querySelector("#player") as HTMLElement;
            if (!playerEl) return false;
            const adContainer = playerEl.querySelector(".fp-ad-container") as HTMLElement;
            if (!adContainer) return false;
            return !!adContainer.querySelector("iframe, .fp-engine");
        };

        const lockContentAtStart = () => {
            if (contentLockIntervalId) return;

            if (!isDisposed) setIsWaitingForAd(true);

            const lockOnce = () => {
                const contentVideoEl = getContentVideoEl();
                if (contentVideoEl) {
                    if (!contentVideoEl.paused) contentVideoEl.pause();
                    if (contentVideoEl.currentTime > 0.05) {
                        safeSetCurrentTime(contentVideoEl, 0);
                    }
                }
            };

            lockOnce();

            contentLockIntervalId = window.setInterval(() => {
                if (currentPlayingVideo !== index) {
                    clearAdWaitTimers();
                    return;
                }

                if (adStarted || isAdContainerActive()) {
                    adStarted = true;
                    clearAdWaitTimers();
                    return;
                }

                lockOnce();
            }, 100);
        };

        const pauseAll = () => {
            const api = coerceFlowplayerApi(playerApiRef.current) || playerApiRef.current;
            if (api && typeof api.pause === "function") {
                safeApiPause(api);
                return;
            }
            const videoEl = getContentVideoEl();
            if (videoEl && !videoEl.paused) videoEl.pause();
        };

        const resolveApi = () => {
            let api = coerceFlowplayerApi(playerApiRef.current);
            if (!api) {
                const resolved = resolveFlowplayerApiFromDom();
                if (resolved) {
                    api = resolved;
                    playerApiRef.current = resolved;
                } else {
                    api = playerApiRef.current;
                }
            }
            return api;
        };

        const triggerPlay = (api: any) => {
            const resumeContent = () => {
                if (isDisposed || currentPlayingVideo !== index) return;
                clearAdWaitTimers();
                safeApiPlay(api);
            };

            const resumeContentAndDisableAds = () => {
                if (isDisposed || currentPlayingVideo !== index) return;
                clearAdWaitTimers();
                safeApiSetOpts(api, { ima: null });
                safeApiPlay(api);
            };

            if (hasPrerollAds) {
                if (!api || typeof api.play !== "function") {
                    clearAdWaitTimers();
                    const videoEl = getContentVideoEl();
                    if (videoEl && videoEl.paused) {
                        safeVideoPlay(videoEl);
                        return true;
                    }
                    return false;
                }

                const isImaSdkLoaded = () => {
                    try {
                        if (typeof window === "undefined") return false;
                        const w = window as any;
                        if (!w.google) return false;
                        const ima = w.google.ima;
                        return !!ima && (typeof ima === "object" || typeof ima === "function");
                    } catch {
                        return false;
                    }
                };

                const adStartTimeoutMs = isImaSdkLoaded() ? 10000 : 300;

                if (!completedListenerAttached && api.ads && typeof api.ads.on === "function") {
                    completedListenerAttached = true;
                    api.ads.on("ad-completed", () => {
                        if (currentPlayingVideo === index) {
                            safeApiPlay(api);
                        }
                    });

                    api.ads.on("ad-error", resumeContent);
                    api.ads.on("ad-blocked", resumeContent);
                    api.ads.on("ad-request-error", resumeContent);
                    api.ads.on("ad-adapter-init-failed", resumeContent);
                }

                if (!playTriggered) {
                    playTriggered = true;
                    lockContentAtStart();
                    safeApiPlay(api);

                    fallbackTimeoutId = window.setTimeout(() => {
                        if (!adStarted && currentPlayingVideo === index) {
                            resumeContentAndDisableAds();
                        }
                    }, adStartTimeoutMs);
                }

                return true;
            }

            // NO-PREROLL PATH
            if (api && typeof api.play === "function") {
                safeApiPlay(api);
                return true;
            }

            const videoEl = getContentVideoEl();
            if (videoEl && videoEl.paused) {
                safeVideoPlay(videoEl);
                return true;
            }

            return false;
        };

        const startPlayback = () => {
            const api = resolveApi();
            const videoEl = getContentVideoEl();

            // If neither API nor video element exist, the player hasn't mounted yet.
            // Start a retry loop waiting for it instead of giving up.
            if (!api && !videoEl) {
                if (!playTriggered) {
                    playTriggered = true;
                    let waitRetryCount = 0;
                    waitIntervalId = window.setInterval(() => {
                        if (isDisposed || currentPlayingVideo !== index) {
                            clearInterval(waitIntervalId);
                            waitIntervalId = undefined;
                            return;
                        }
                        waitRetryCount++;

                        const retryApi = resolveApi();
                        const retryVideoEl = getContentVideoEl();

                        if (retryApi || retryVideoEl) {
                            clearInterval(waitIntervalId);
                            waitIntervalId = undefined;
                            playTriggered = false; // allow triggerPlay to set it properly for ads
                            triggerPlay(retryApi);
                        } else if (waitRetryCount > 50) { // 5 seconds max
                            clearInterval(waitIntervalId);
                            waitIntervalId = undefined;
                        }
                    }, 100);
                }
                return false;
            }

            return triggerPlay(api);
        };

        if (!isActive) {
            pauseAll();
            clearAdWaitTimers();
            return;
        }

        if (!allowManualAutoplay) {
            clearAdWaitTimers();
            return;
        }

        let retryTimer: number | undefined;

        const timeoutId = window.setTimeout(() => {
            const started = startPlayback();

            if (!started) {
                const start = Date.now();
                retryTimer = window.setInterval(() => {
                    if (Date.now() - start > 2500) {
                        if (retryTimer) clearInterval(retryTimer);
                        return;
                    }

                    const ok = startPlayback();
                    if (ok && retryTimer) clearInterval(retryTimer);
                }, 100);
            }
        }, 300);

        return () => {
            isDisposed = true;
            clearTimeout(timeoutId);
            if (retryTimer) clearInterval(retryTimer);
            if (waitIntervalId) clearInterval(waitIntervalId);
            clearAdWaitTimers();
        };
    }, [currentPlayingVideo, playerReady]);

    // Reset playerReady when the component unmounts so next mount starts fresh
    useEffect(() => {
        return () => { setPlayerReady(false); };
    }, []);

    return {
        playerMedia,
        isWaitingForAd,
        shouldDisplayVideo,
        hasPrerollAds,
        playerApiRef,
        onPlayerLoaded,
        handleVideoEnded,
    };
};
